import React, { Component } from "react";
import PropTypes from "prop-types";
import { Card, Form, Select, Button, Input, InputNumber, Popover, Tooltip, Collapse } from "antd";
import { SwapOutlined, ArrowsAltOutlined, FullscreenOutlined, ArrowDownOutlined, ArrowUpOutlined, QuestionCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { withRouter } from "react-router";
const FormItem = Form.Item;
const { TextArea } = Input;
const Option = Select.Option;
const { Panel } = Collapse;
import PubsInclusion from "../../common/pubs-inclusion";
import { toUpper, isNumber, isArray } from "lodash";
import DateRangePicker from "../../common/date-range-picker";
import MultiSelect from "../../common/multi-select";
import {
  getDistinctValues,
  getDistinctFamilyID,
  expandPublicationNumbersByFamilies,
  expandPublicationNumbersByCitedBy,
  expandPublicationNumbersByCiting,
  checkPublicationInclusion,
  checkFamilyIdInclusion
} from "../../../services/api-landscape";
import { transformPublicationNumbers } from "../../../services/api-patents";
import { keepOnlyLandscapeQueryStrings } from "../../../helpers/url";
import { handleCountryFilter, countByValues } from "./utils";
import FullTextHelp from "../../common/full-text-help";
import SearchIn from "../../common/search-in";

const styles = {
  body: {
    width: "100%",
    padding: 30
  },
  sider: {
    background: "transparent",
    marginRight: 16
  },
  verticalGutter: {
    marginTop: 16
  }
};

const propsToStates = q => ({
  alphaThreshold: q.alpha_threshold,
  techs: q.techs,
  pubNumber: q.pub_number,
  pubStatus: q.pub_status,
  familyId: q.family_id,
  extendedFamilyId: q.extended_family_id,
  assigneeCurrent: q.assignee_current,
  assigneeOriginal: q.assignee_original,
  parentCompany: q.assignee_parent,
  inventors: q.inventors,
  country: q.country,
  countryPop: q.country_pop,
  cpcCodes: q.cpc_codes,
  uspcCodes: q.uspc_codes,
  filingDate: q.application_date,
  priorityDate: q.priority_date,
  publicationDate: q.publication_date,
  expirationDate: q.expiration_date,
  isDeclared: q.declared_as_sep // boolean strings ('0' and '1') to ints
    ? isArray(q.declared_as_sep)
      ? q.declared_as_sep.map(v => Number(v))
      : Number(q.declared_as_sep)
    : undefined,
  pools: q.pools,
  countBy: q.count_by,
  // boolean operators
  inverted: q.not || [],
  fullText: q.full_text,
  searchIn: q.full_text_fields,
  "3gppSpec": q["3gpp.spec"],
  "3gppSpecSeries": q["3gpp.spec_series"],
  "3gppSpecDate2G": q["3gpp.2g_declaration_date"],
  "3gppSpecDate3G": q["3gpp.3g_declaration_date"],
  "3gppSpecDate4G": q["3gpp.4g_declaration_date"],
  "3gppSpecDate5G": q["3gpp.5g_declaration_date"]
});

const stateKey2Field = {
  techs: "techs",
  pools: "pools",
  isDeclared: "declared_as_sep",
  familyId: "family_id",
  extendedFamilyId: "extended_family_id",
  assigneeCurrent: "assignee_current",
  assigneeOriginal: "assignee_original",
  parentCompany: "assignee_parent",
  inventors: "inventors",
  country: "country",
  countryPop: "country",
  cpcCodes: "cpc_codes",
  uspcCodes: "uspc_codes",
  pubStatus: "publication_status",
  "3gppSpec": "3gpp.spec",
  "3gppSpecSeries": "3gpp.spec_series",
  "3gppSpecDate2G": "3gpp.2g_declaration_date",
  "3gppSpecDate3G": "3gpp.3g_declaration_date",
  "3gppSpecDate4G": "3gpp.4g_declaration_date",
  "3gppSpecDate5G": "3gpp.5g_declaration_date"
};

class LandscapeToolbar extends Component {
  static propTypes = {
    esUrl: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    defaultFilters: PropTypes.shape({
      count_by: PropTypes.oneOf(countByValues),
      pub_status: PropTypes.arrayOf(PropTypes.string),
      alpha_threshold: PropTypes.number,
      pools: PropTypes.arrayOf(PropTypes.string)
    }),
    hiddenFilters: PropTypes.arrayOf(PropTypes.oneOf(["techs", "spec", "spec_series", "declared_as_sep", "pools"])),
    accessLevel: PropTypes.oneOf(["full", "trial", "guest", "public"]).isRequired
  };

  static defaultProps = {
    hiddenFilters: ["techs", "3gpp"]
  };

  state = {
    ...propsToStates(keepOnlyLandscapeQueryStrings(this.props.location.query)),
    pubNumberTransformLoading: false,
    pubNumberExpandLoading: false
  };

  componentDidMount() {
    this._isMounted = true;
    this._timeout = 0;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState(propsToStates(keepOnlyLandscapeQueryStrings(nextProps.location.query)));
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const { accessLevel, esUrl } = this.props;
    const hiddenFilters = this.props.hiddenFilters.concat(["declared_as_sep"]);
    const isNotFullAccess = accessLevel !== "full";
    const { countBy, pubNumberTransformLoading, pubNumberExpandLoading, alphaThreshold, country, countryPop } = this.state;

    const resetButton = (
      <Button size="small" onClick={this._resetFilter}>
        {"Reset"}
      </Button>
    );
    const resetButtonPopulation = (
      <Button size="small" onClick={this._resetFilterPopulation}>
        {"Reset"}
      </Button>
    );
    const alphaScoreExplanation = (
      <div>
        <p>
          The Similarity Score is a number between 0 and 100 that measures the degree of textual similarity between any given patent and the set of True Positives. The higher the score, the more
          similar that patent is – in terms of its text-based features – to the independently-verified list of MPEG-LA and HEVC Advance patents. Our approach only looks at the patent title, abstract,
          description, and claims, ignoring any non-text features such as filing date, classification codes, forward citations, etc.
        </p>
        <p>
          The algorithm is trained until as many True Positives as possible exceed a score of 50 ("Baseline"). In the histogram below, which plots the similarity scores of the training set for
          similarity >= 15, the True Positives are depicted in blue while the True Negatives are depicted in red.
        </p>
      </div>
    );
    const alphaScoreLabel = (
      <span>
        {"SIMILARITY SCORE "}
        <Popover
          placement="rightBottom"
          title={"What is the SIMILARITY Score?"}
          content={alphaScoreExplanation}
          trigger="click"
          getPopupContainer={trigger => trigger.parentNode.parentNode.parentNode.parentNode}
          overlayStyle={{ minWidth: 300 }}
        >
          <QuestionCircleOutlined style={{ cursor: "pointer" }} />
        </Popover>
      </span>
    );

    const fullTextLabel = (
      <div style={{ display: "flex", justifyContent: "space-between", minWidth: 210 }}>
        <div>
          {"FULL TEXT "}
          <FullTextHelp />
        </div>
        <SearchIn searchIn={this.state.searchIn} onChange={this._updateState("searchIn")} disabled={isNotFullAccess} />
      </div>
    );
    const labelPubNum = (
      <div style={{ display: "flex", justifyContent: "space-between", minWidth: 210 }}>
        <div>{"PUB. NUM"}</div>
        <Button.Group size="small" disabled={isNotFullAccess}>
          <Tooltip title="Transform Publication Numbers to DOCDB format" placement="right">
            <Button icon={<SwapOutlined />} onClick={this._transformPubNumber} loading={pubNumberTransformLoading} />
          </Tooltip>
          <Tooltip title="Expand Publication Numbers by Simple Families (Up to 10k)" placement="right">
            <Button icon={<ArrowsAltOutlined />} onClick={() => this._expandPubNumber("simple_family")} loading={pubNumberExpandLoading} />
          </Tooltip>
          <Tooltip title="Expand Publication Numbers by Extended Families (Up to 10k)" placement="right">
            <Button icon={<FullscreenOutlined />} onClick={() => this._expandPubNumber("extended_family")} loading={pubNumberExpandLoading} />
          </Tooltip>
          <Tooltip title="Expand Publication Numbers by Forward Citations (Up to 10k)" placement="right">
            <Button icon={<ArrowUpOutlined />} onClick={() => this._expandPubNumber("forward")} loading={pubNumberExpandLoading} />
          </Tooltip>
          <Tooltip title="Expand Publication Numbers by Backward Citations (Up to 10k)" placement="right">
            <Button icon={<ArrowDownOutlined />} onClick={() => this._expandPubNumber("backward")} loading={pubNumberExpandLoading} />
          </Tooltip>
        </Button.Group>
      </div>
    );
    const labelCollection = (
      <div style={{ display: "flex", justifyContent: "space-between", minWidth: 210 }}>
        <div>
          {"COLLECTIONS "}
        </div>
        <Tooltip title="Invert selection of collections." placement="right">
          <Button
            size="small"
            icon={<SyncOutlined />}
            disabled={isNotFullAccess}
            type={this.state.inverted.indexOf("pools") >= 0 ? "primary" : "default"}
            onClick={() => this.setState({ inverted: this.state.inverted.indexOf("pools") >= 0 ? [] : ["pools"] }, this._updateRouter)}
          />
        </Tooltip>
      </div>
    );
    const helpCountry = handleCountryFilter(country, countryPop) === "Invalid" ? { help: "Selection conflicts with Landscape Settings", validateStatus: "error" } : {};
    const wildcardHelp = (
      <table>
        <tbody>
          <tr>
            <td>
              <strong>Symbol</strong>
            </td>
            <td>
              <strong>Operation</strong>
            </td>
          </tr>
          <tr>
            <td>
              <code>&quot;</code>
            </td>
            <td>exact phrase</td>
          </tr>
          <tr>
            <td>
              <code>?</code>
            </td>
            <td>match any single character</td>
          </tr>
          <tr>
            <td>
              <code>*</code>
            </td>
            <td>match zero or more characters</td>
          </tr>
          <tr>
            <td colSpan="2">Assignee and codes filters also support this syntax</td>
          </tr>
        </tbody>
      </table>
    );
    const parentCompanyLabel = (
      <span>
        {"PARENT COMPANY "}
        <Popover placement="rightBottom" title={"Wildcard Query Syntax"} content={wildcardHelp} trigger="click">
          <QuestionCircleOutlined style={{ cursor: "pointer" }} />
        </Popover>
      </span>
    );
    return (
      <div>
        <Card title="Landscape Settings" extra={resetButtonPopulation} style={styles.verticalGutter}>
          <Form layout="vertical">
            <FormItem label="COUNT BY">
              <Select value={countBy} onChange={this._updateState("countBy")} getPopupContainer={trigger => trigger.parentNode}>
                <Option value="pub">{"Grants & Applications"}</Option>
                <Option value="grant">{"Grants"}</Option>
                <Option value="app">{"Applications"}</Option>
                <Option value="family">{"Families"}</Option>
                <Option value="family_grant">{"Families with Grants"}</Option>
                <Option value="family_ext">{"Extended Families"}</Option>
                <Option value="family_ext_grant">{"Extended Families with Grants"}</Option>
              </Select>
            </FormItem>
            <FormItem label="STATUS">{this._renderSelect("pubStatus", false, "e.g. Active")}</FormItem>
            {hiddenFilters.indexOf("techs") < 0 ? <FormItem label="TECHNOLOGIES">{this._renderSelect("techs", false, "")}</FormItem> : null}
            <FormItem label={alphaScoreLabel}>
              <InputNumber value={alphaThreshold} min={0} max={100} onChange={this._updateAlphaInput} />
            </FormItem>
            <FormItem label="COUNTRY">{this._renderSelect("countryPop", isNotFullAccess, "e.g. US", undefined, toUpper)}</FormItem>
            {hiddenFilters.indexOf("declared_as_sep") < 0 ? (
              <FormItem label="DECLARED AS SEP">{this._renderSelect("isDeclared", isNotFullAccess, "e.g. true", [0, 1], v => ["false", "true"][v], "multiple")}</FormItem>
            ) : null}
          </Form>
        </Card>

        <Card title="Subset Filters" extra={resetButton} style={styles.verticalGutter} styles={{ body: { padding: 0 } }}>
          <Form layout="vertical">
            <Collapse defaultActiveKey={["basic", "spec"]} bordered={false}>
              <Panel header="BASIC" key="basic">
                {hiddenFilters.indexOf("pools") < 0 ? (
                  <FormItem label={labelCollection}>
                    {this._renderSelect("pools", false, "Patent pools or collections", undefined, toUpper, "multiple", undefined, true)}
                  </FormItem>
                ) : null}
                <FormItem label={fullTextLabel}>
                  <Input
                    placeholder="Full Text Search"
                    onChange={e => this.setState({ fullText: e.target.value })}
                    onPressEnter={this._updateRouter}
                    value={this.state.fullText}
                    disabled={isNotFullAccess}
                  />
                </FormItem>
                <FormItem
                  label={labelPubNum}
                  extra={<PubsInclusion esUrl={esUrl} pubs={this._getTextAreaContent("pubNumber")} onChange={(esUrl, pubs) => checkPublicationInclusion(esUrl, this.props.version, pubs)} />}
                >
                  <TextArea
                    value={this.state.pubNumber}
                    onChange={e => this._updateTextArea(e, "pubNumber")}
                    placeholder="Please enter one publication number (DOCDB format) per line, without any punctuation. e.g. US-9462290-B2"
                    autoSize={{ minRows: 3, maxRows: 10 }}
                    disabled={isNotFullAccess}
                  />
                </FormItem>
                <FormItem
                  label="FAMILY ID"
                  extra={<PubsInclusion esUrl={esUrl} pubs={this._getTextAreaContent("familyId")} onChange={(esUrl, familyIds) => checkFamilyIdInclusion(esUrl, this.props.version, familyIds)} />}
                >
                  <TextArea
                    value={this.state.familyId}
                    onChange={e => this._updateTextArea(e, "familyId")}
                    placeholder="Please enter one family id per line, without any punctuation. e.g. 35159775"
                    autoSize={{ minRows: 3, maxRows: 10 }}
                    disabled={isNotFullAccess}
                  />
                </FormItem>
                <FormItem label="EXTENDED FAMILY ID">
                  <TextArea
                    value={this.state.extendedFamilyId}
                    onChange={e => this._updateTextArea(e, "extendedFamilyId")}
                    placeholder="Please enter one family id per line, without any punctuation. e.g. 46653056"
                    autoSize={{ minRows: 3, maxRows: 10 }}
                    disabled={isNotFullAccess}
                  />
                </FormItem>
                <FormItem label="COUNTRY" {...helpCountry}>
                  {this._renderSelect("country", isNotFullAccess, "e.g. US", undefined, toUpper)}
                </FormItem>
                <FormItem label="CPC CODES">{this._renderSelect("cpcCodes", isNotFullAccess, "e.g. H04N19/176", undefined, toUpper, undefined, undefined, true)}</FormItem>
                <FormItem label="USPC CODES">{this._renderSelect("uspcCodes", isNotFullAccess, "e.g. 382/232.", undefined, toUpper, undefined, undefined, true)}</FormItem>
              </Panel>
              <Panel header="ENTITIES" key="entities">
                <FormItem label={parentCompanyLabel}>{this._renderSelect("parentCompany", isNotFullAccess, "e.g. Samsung")}</FormItem>
                <FormItem label="CURRENT ASSIGNEE">{this._renderSelect("assigneeCurrent", isNotFullAccess, "e.g. Samsung")}</FormItem>
                <FormItem label="ORIGINAL ASSIGNEE">{this._renderSelect("assigneeOriginal", isNotFullAccess, "e.g. Canon")}</FormItem>
                <FormItem label="INVENTORS">{this._renderSelect("inventors", isNotFullAccess, "e.g. Park Sung Jun")}</FormItem>
              </Panel>
              <Panel header="DATES" key="dates">
                {this._renderDateFormItem("filingDate", "FILING DATE", isNotFullAccess)}
                {this._renderDateFormItem("priorityDate", "PRIORITY DATE", isNotFullAccess)}
                {this._renderDateFormItem("publicationDate", "PUBLICATION DATE", isNotFullAccess)}
                {this._renderDateFormItem("expirationDate", "EXPIRATION DATE", isNotFullAccess)}
              </Panel>
              {hiddenFilters.indexOf("3gpp") < 0 ? (
                <Panel header="SPECIFICATIONS" key="spec">
                  <FormItem label="SPECIFICATION">{this._renderSelect("3gppSpec", isNotFullAccess, "e.g. TS36.211", undefined, toUpper)}</FormItem>
                  <FormItem label="SPECIFICATION SERIES">{this._renderSelect("3gppSpecSeries", isNotFullAccess, "e.g. 36")}</FormItem>
                  {this._renderDateFormItem("3gppSpecDate2G", "2G DECLARATION DATE", isNotFullAccess)}
                  {this._renderDateFormItem("3gppSpecDate3G", "3G DECLARATION DATE", isNotFullAccess)}
                  {this._renderDateFormItem("3gppSpecDate4G", "4G DECLARATION DATE", isNotFullAccess)}
                  {this._renderDateFormItem("3gppSpecDate5G", "5G DECLARATION DATE", isNotFullAccess)}
                </Panel>
              ) : null}
            </Collapse>
          </Form>
        </Card>
      </div>
    );
  }

  _renderSelect = (key, disabled, placeholder, options, valueRenderer, mode, tokenSeparators, showSearch = false) => {
    return (
      <MultiSelect
        value={this.state[key]}
        onChange={this._updateState(key)}
        options={options}
        optionApiCall={
          options
            ? undefined
            : val =>
              getDistinctValues(this.props.esUrl, this.props.version, stateKey2Field[key], {
                [stateKey2Field[key]]: val || ""
              })
                .then(opts => opts.filter(o => toUpper(o).indexOf(toUpper(val || "")) >= 0))
                .catch(() => [])
        }
        optionRender={valueRenderer}
        mode={mode}
        placeholder={placeholder}
        disabled={disabled}
        tokenSeparators={tokenSeparators}
        showSearch={showSearch}
      />
    );
  };

  _renderDateFormItem = (key, label, disabled) => {
    return (
      <FormItem label={label}>
        <DateRangePicker value={this.state[key]} onChange={this._updateState(key)} disabled={disabled} allowClear={true} />
      </FormItem>
    );
  };

  _resetFilterPopulation = () => {
    if (this._isMounted)
      this.setState(
        {
          alphaThreshold: this.props.defaultFilters.alpha_threshold,
          countBy: this.props.defaultFilters.count_by,
          pubStatus: this.props.defaultFilters.pub_status,
          techs: undefined,
          countryPop: undefined,
          isDeclared: undefined
        },
        this._updateRouter
      );
  };

  _updateAlphaInput = val => {
    if (isNumber(val) || !val) {
      if (this._isMounted) this.setState({ alphaThreshold: val }, this._runAfterTyping(this._updateRouter));
    }
  };

  _resetFilter = () => {
    if (this._isMounted)
      this.setState(
        {
          pubNumber: undefined,
          familyId: undefined,
          assigneeCurrent: undefined,
          assigneeOriginal: undefined,
          parentCompany: undefined,
          inventors: undefined,
          country: undefined,
          cpcCodes: undefined,
          uspcCodes: undefined,
          filingDate: undefined,
          priorityDate: undefined,
          publicationDate: undefined,
          expirationDate: undefined,
          pools: this.props.defaultFilters.pools,
          inverted: [],
          fullText: undefined,
          searchIn: undefined,
          "3gppSpec": undefined,
          "3gppSpecSeries": undefined,
          "3gppSpecDate2G": undefined,
          "3gppSpecDate3G": undefined,
          "3gppSpecDate4G": undefined,
          "3gppSpecDate5G": undefined
        },
        this._updateRouter
      );
  };

  _updateTextArea = (e, field) => {
    if (this._isMounted)
      this.setState(
        {
          [field]: e.target.value == "" ? undefined : e.target.value
        },
        this._runAfterTyping(this._updateRouter)
      );
  };

  _getTextAreaContent = field =>
    this.state[field]
      ? this.state[field]
        .trim()
        .split(/\r?\n/)
        .map(e => e.trim())
        .filter(s => s.length > 0)
      : undefined;

  _transformPubNumber = () => {
    const pubs = this._getTextAreaContent("pubNumber");
    if (pubs && pubs.length) {
      if (this._isMounted)
        this.setState({
          pubNumberTransformLoading: true
        });
      transformPublicationNumbers(pubs)
        .then(pubsNew => {
          if (this._isMounted)
            this.setState(
              {
                pubNumber: pubsNew && pubsNew.length ? pubsNew.join("\n") : undefined,
                pubNumberTransformLoading: false
              },
              this._updateRouter
            );
        })
        .catch(() => {
          if (this._isMounted)
            this.setState({
              pubNumber: pubs.join("\n"),
              pubNumberTransformLoading: false
            });
        });
    }
  };

  _expandPubNumber = mode => {
    const pubs = this._getTextAreaContent("pubNumber");
    const { esUrl, version } = this.props;
    const api = {
      simple_family: pubs => getDistinctFamilyID(esUrl, version, pubs).then(familyIDs => expandPublicationNumbersByFamilies(esUrl, version, familyIDs)),
      extended_family: pubs => getDistinctFamilyID(esUrl, version, pubs, true).then(familyIDs => expandPublicationNumbersByFamilies(esUrl, version, familyIDs, true)),
      forward: pubs => expandPublicationNumbersByCitedBy(esUrl, version, pubs),
      backward: pubs => expandPublicationNumbersByCiting(esUrl, version, pubs)
    };
    if (pubs && pubs.length) {
      if (this._isMounted)
        this.setState({
          pubNumberExpandLoading: true
        });
      transformPublicationNumbers(pubs)
        .then(pubsNew => api[mode](pubsNew))
        .then(pubNumbers => {
          if (this._isMounted)
            this.setState(
              {
                pubNumber: pubNumbers && pubNumbers.length ? pubNumbers.join("\n") : undefined,
                pubNumberExpandLoading: false
              },
              this._updateRouter
            );
        })
        .catch(() => {
          if (this._isMounted)
            this.setState({
              pubNumber: pubs.join("\n"),
              pubNumberTransformLoading: false
            });
        });
    }
  };

  _runAfterTyping = (func, interval = 800) => {
    if (this._timeout) clearTimeout(this._timeout);
    this._timeout = setTimeout(func, interval);
  };

  _updateState = field => {
    return val => {
      if (this._isMounted) this.setState({ [field]: val }, this._updateRouter);
    };
  };

  _updateRouter = () => {
    this.props.router.push({
      pathname: this.props.location.pathname,
      query: {
        alpha_threshold: this.state.alphaThreshold,
        techs: this.state.techs,
        pub_number: this.state.pubNumber,
        pub_status: this.state.pubStatus,
        family_id: this.state.familyId,
        extended_family_id: this.state.extendedFamilyId,
        assignee_current: this.state.assigneeCurrent,
        assignee_original: this.state.assigneeOriginal,
        assignee_parent: this.state.parentCompany,
        inventors: this.state.inventors,
        country: this.state.country,
        country_pop: this.state.countryPop,
        cpc_codes: this.state.cpcCodes,
        uspc_codes: this.state.uspcCodes,
        application_date: this.state.filingDate,
        priority_date: this.state.priorityDate,
        publication_date: this.state.publicationDate,
        expiration_date: this.state.expirationDate,
        pools: this.state.pools,
        count_by: this.state.countBy,
        declared_as_sep: this.state.isDeclared,
        // boolean operator
        not: this.state.inverted,
        // full text
        full_text: this.state.fullText,
        full_text_fields: this.state.searchIn,
        // specification related
        "3gpp.spec": this.state["3gppSpec"],
        "3gpp.spec_series": this.state["3gppSpecSeries"],
        "3gpp.2g_declaration_date": this.state["3gppSpecDate2G"],
        "3gpp.3g_declaration_date": this.state["3gppSpecDate3G"],
        "3gpp.4g_declaration_date": this.state["3gppSpecDate4G"],
        "3gpp.5g_declaration_date": this.state["3gppSpecDate5G"]
      }
    });
  };
}

export default withRouter(LandscapeToolbar);
